import type { PoGetSystemStatusResponse } from '@repo/sdk/gen'
import { Badge, badgeVariants } from '@repo/ui/badge'
import { Card, CardContent } from '@repo/ui/card'
import { formatLocaleDate } from '@repo/ui/dates'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/tooltip'
import { cn } from '@repo/ui/utils'
import { AlertTriangle, Database, LoaderCircle, Radio } from 'lucide-react'
import type { ReactNode } from 'react'
import { staleDays } from '@/lib/data-freshness'
import { useTranslation } from '@/lib/i18n'

// The dashboard's two status cards: the data «Genera» reads and the link with
// the agent that sends it.

// The data «Genera» reads: the last complete sync, table by table, each with
// when a sync last confirmed it. A sync still in flight never shows here until
// it closes; a table it has already brought is flagged as on its way. The date
// is labelled neutrally: whether the data is up to date is the banner's call,
// and the tables left behind are named next to it.
function DataCard(props: {
	dataset: PoGetSystemStatusResponse['data']['dataset']
	updating: boolean
	tables: PoGetSystemStatusResponse['data']['tables']
}) {
	const { t } = useTranslation()
	const asOf = props.dataset.asOf
	const behind = new Set(
		props.dataset.tablesBehind.map((table) => table.tableName)
	)

	return (
		<Card className='py-0'>
			<CardContent className='flex flex-col gap-3 py-5'>
				<div className='flex flex-wrap items-center justify-between gap-x-2 gap-y-1'>
					<div className='flex items-center gap-2 font-medium'>
						<Database className='size-4 text-muted-foreground' />
						{t('po:dashboard.data_freshness')}
					</div>
					{props.updating ? (
						<span className='flex items-center gap-1.5 whitespace-nowrap text-muted-foreground text-xs'>
							<LoaderCircle className='size-3.5 animate-spin' />
							{t('po:dashboard.sync_in_progress')}
						</span>
					) : null}
				</div>
				{asOf ? (
					<>
						<div className='flex flex-col gap-1'>
							<InfoRow
								label={t('po:dashboard.dataset_label')}
								value={formatLocaleDate(asOf, 'P HH:mm')}
							/>
							{props.dataset.tablesBehind.map((table) => (
								<p
									className='text-amber-700 text-xs dark:text-amber-400'
									key={table.tableName}
								>
									{t('po:dataset.table_behind', {
										table: table.tableName,
										date: formatLocaleDate(
											table.confirmedAt,
											'P HH:mm'
										)
									})}
								</p>
							))}
						</div>
						<div className='flex flex-col'>
							<div className='flex items-center justify-between gap-2 border-b pb-1.5 text-muted-foreground text-xs'>
								<span>{t('po:dashboard.tables_heading')}</span>
								<span>
									{t('po:dashboard.confirmed_heading')}
								</span>
							</div>
							<ul className='flex flex-col divide-y'>
								{props.tables.map((table) => (
									<TableRow
										behind={behind.has(table.tableName)}
										key={table.tableName}
										table={table}
									/>
								))}
							</ul>
						</div>
					</>
				) : (
					<p className='text-muted-foreground text-sm'>
						{t('po:dashboard.waiting_first_data')}
					</p>
				)}
			</CardContent>
		</Card>
	)
}

// What sets a table apart is written under its name, never only in a tooltip,
// which a touch screen never opens: the agent's reason for a table it failed
// on, or else that the table was left behind the dataset's date. A table left
// behind, or unconfirmed for over a day, has its date in amber; the second
// case only comes with a stale dataset, which the app shell's banner states on
// every screen, so its hint stays in a tooltip.
function TableRow(props: {
	table: PoGetSystemStatusResponse['data']['tables'][number]
	behind: boolean
}) {
	const { t } = useTranslation()
	const table = props.table
	const confirmedAt = formatLocaleDate(table.confirmedAt, 'P HH:mm')
	const stale = staleDays(table.confirmedAt) !== null

	return (
		<li className='flex items-center justify-between gap-2 py-1.5 text-sm'>
			<span className='flex min-w-0 flex-col gap-0.5'>
				{/* On a narrow card the badge wraps under the name, never over
				    the date. */}
				<span className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
					<span className='truncate font-mono text-xs'>
						{table.tableName}
					</span>
					{table.newerVersionPending ? (
						<Tooltip>
							<TooltipTrigger
								className={cn(
									badgeVariants({ variant: 'secondary' }),
									'cursor-default bg-sky-500/15 text-sky-700 dark:bg-sky-400/15 dark:text-sky-300'
								)}
							>
								{t('po:dashboard.newer_version_pending')}
							</TooltipTrigger>
							<TooltipContent>
								{t('po:dashboard.newer_version_pending_hint')}
							</TooltipContent>
						</Tooltip>
					) : null}
				</span>
				{table.lastOutcome === 'failed' && (
					<span className='text-amber-700 text-xs dark:text-amber-400'>
						{table.lastFailureReason
							? t('po:dashboard.table_failed', {
									reason: table.lastFailureReason
								})
							: t('po:dashboard.table_failed_no_reason')}
					</span>
				)}
				{/* The failure already says why the table stayed behind. */}
				{props.behind && table.lastOutcome !== 'failed' && (
					<span className='text-amber-700 text-xs dark:text-amber-400'>
						{t('po:dashboard.table_behind')}
					</span>
				)}
			</span>
			{stale ? (
				<Tooltip>
					<TooltipTrigger className='flex shrink-0 cursor-default items-center gap-1 text-amber-700 tabular-nums dark:text-amber-400'>
						<AlertTriangle className='size-3.5' />
						{confirmedAt}
					</TooltipTrigger>
					<TooltipContent>
						{t('po:dashboard.table_stale_hint')}
					</TooltipContent>
				</Tooltip>
			) : (
				<span
					className={cn(
						'flex shrink-0 items-center gap-1 tabular-nums',
						props.behind
							? 'text-amber-700 dark:text-amber-400'
							: 'text-muted-foreground'
					)}
				>
					{props.behind ? (
						<AlertTriangle className='size-3.5' />
					) : null}
					{confirmedAt}
				</span>
			)}
		</li>
	)
}

function AgentCard(props: {
	agent: PoGetSystemStatusResponse['data']['agent']
}) {
	const { t } = useTranslation()
	const agent = props.agent
	return (
		<Card className='py-0'>
			<CardContent className='flex flex-col gap-3 py-5'>
				<div className='flex items-center gap-2 font-medium'>
					<Radio className='size-4 text-muted-foreground' />
					{t('po:dashboard.agent')}
				</div>
				{agent ? (
					<>
						<InfoRow
							label={t('po:dashboard.agent_name_label')}
							value={
								<span className='flex items-center gap-2'>
									{agent.name}
									{agent.version ? (
										<Badge variant='outline'>
											{agent.version}
										</Badge>
									) : null}
								</span>
							}
						/>
						<InfoRow
							label={t('po:dashboard.agent_seen_label')}
							value={
								agent.lastSeenAt
									? formatLocaleDate(
											agent.lastSeenAt,
											'P HH:mm'
										)
									: t('po:dashboard.never')
							}
						/>
						<InfoRow
							label={t('po:dashboard.agent_full_label')}
							value={
								agent.lastFullSyncAt
									? formatLocaleDate(
											agent.lastFullSyncAt,
											'P HH:mm'
										)
									: t('po:dashboard.never')
							}
						/>
					</>
				) : (
					<p className='text-muted-foreground text-sm'>
						{t('po:dashboard.no_agent_hint')}
					</p>
				)}
			</CardContent>
		</Card>
	)
}

function InfoRow(props: { label: string; value: ReactNode }) {
	return (
		<div className='flex items-center justify-between gap-2 text-sm'>
			<span className='text-muted-foreground'>{props.label}</span>
			<span className='truncate text-right font-medium'>
				{props.value}
			</span>
		</div>
	)
}

export { AgentCard, DataCard }
